import React, { useState, useCallback } from 'react';

const API = window.location.origin;

// One-shot diagnostics dump for post-incident triage. Renders the JSON
// as plain text with a Copy JSON button — screenshots truncate large
// payloads (event_log up to 200, open_orders, peak_path arrays), so
// clipboard is the reliable path.
export const DiagnosticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');

  const fetchDiagnostics = useCallback(async () => {
    setLoading(true); setError(null); setCopyStatus('');
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/grid-bot/diagnostics`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  const copyJson = useCallback(async () => {
    if (!data) return;
    const s = JSON.stringify(data, null, 2);
    try {
      await navigator.clipboard.writeText(s);
      setCopyStatus(`Copied ${s.length.toLocaleString()} chars`);
    } catch (e) {
      // Fallback for older browsers / non-HTTPS previews.
      const ta = document.createElement('textarea');
      ta.value = s; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try {
        document.execCommand('copy');
        setCopyStatus(`Copied ${s.length.toLocaleString()} chars`);
      } catch (e2) {
        setCopyStatus(`Copy failed: ${e2.message}`);
      } finally { document.body.removeChild(ta); }
    }
    setTimeout(() => setCopyStatus(''), 3000);
  }, [data]);

  // Short summary card: version, engine liveness, drift/retract state,
  // resting-order counts. The full JSON below is the source of truth.
  const summary = data ? {
    engine_version: data.engine_version,
    bot_running: data.bot_running,
    last_tick_secs_ago: data.engine?.seconds_since_last_tick,
    drift_pct: data.drift_guard?.current_12h_drift_pct,
    threshold: data.drift_guard?.trend_drift_pause_pct,
    release: data.drift_guard?.release_pct,
    retract_enabled: data.drift_guard?.retract_entries_on_engage,
    long_latched: data.drift_guard?.soft_long_latched,
    short_latched: data.drift_guard?.soft_short_latched,
    open_orders: data.exchange_orders?.total,
    by_type: data.exchange_orders?.by_type,
    ct_stop: data.protective_slots?.ct_stop_oid ? {
      oid: data.protective_slots.ct_stop_oid,
      stage: data.protective_slots.ct_stop_stage,
      trigger: data.protective_slots.ct_stop_trigger_px,
    } : null,
    recent_trades: data.recent_trades?.length,
    event_log_size: data.event_log?.length,
    api_budget: data.api_budget?.available ? {
      remaining: data.api_budget.remaining,
      used: data.api_budget.requests_used,
      cap: data.api_budget.requests_cap,
      status: data.api_budget.status,
      burn_rate_1h_req: data.api_budget.burn_rate_1h_req,
      last_replenish_ts: data.api_budget.last_replenish_ts,
      stale: !!data.api_budget.stale,
      stale_reason: data.api_budget.stale_reason,
      stale_since_sec: data.api_budget.stale_since_sec,
    } : null,
  } : null;

  const wrap = { border: '1px solid #374151', borderRadius: 8, padding: 16, marginTop: 16, background: '#0b1220' };
  const btn = { background: '#60a5fa', color: '#0b1220', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
  const btnGhost = { ...btn, background: '#1f2937', color: '#e5e7eb' };
  const pre = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: '#e5e7eb', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, background: '#0f172a', padding: 12, borderRadius: 6, maxHeight: 720, overflow: 'auto' };
  const summaryBox = { background: '#111827', border: '1px solid #1f2937', borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 12, color: '#d1d5db' };

  return (
    <div style={wrap} data-testid="diagnostics-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: '#f3f4f6', fontSize: 15 }}>Diagnostics</span>
        <button style={btn} onClick={fetchDiagnostics} disabled={loading} data-testid="diagnostics-fetch-btn">
          {loading ? 'Loading…' : 'Fetch state'}
        </button>
        <button style={btnGhost} onClick={copyJson} disabled={!data} data-testid="diagnostics-copy-btn">
          Copy JSON
        </button>
        {copyStatus && (
          <span style={{ color: '#4ade80', fontSize: 12 }} data-testid="diagnostics-copy-status">
            {copyStatus}
          </span>
        )}
        <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 'auto' }}>
          One-shot live state for triage. Copy JSON for the full payload.
        </span>
      </div>
      {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 8 }} data-testid="diagnostics-error">Error: {error}</div>}
      {summary && (
        <div style={summaryBox} data-testid="diagnostics-summary">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
            <div><b>Version:</b> {summary.engine_version}</div>
            <div><b>Bot running:</b> {String(summary.bot_running)}</div>
            <div><b>Last tick:</b> {summary.last_tick_secs_ago?.toFixed?.(1) ?? '—'}s ago</div>
            <div><b>Recent trades:</b> {summary.recent_trades ?? '—'}</div>
            <div><b>12h drift:</b> {summary.drift_pct?.toFixed?.(3) ?? '—'}%</div>
            <div><b>Threshold / release:</b> ±{summary.threshold ?? '—'}% / ±{summary.release?.toFixed?.(3) ?? '—'}%</div>
            <div><b>Retract enabled:</b> {String(summary.retract_enabled)}</div>
            <div><b>Latched L/S:</b> {String(summary.long_latched)} / {String(summary.short_latched)}</div>
            <div><b>Open orders:</b> {summary.open_orders ?? '—'} — {JSON.stringify(summary.by_type || {})}</div>
            <div><b>Event log:</b> {summary.event_log_size ?? '—'}</div>
            <div style={{ gridColumn: '1 / -1' }}>
              <b>CT stop:</b> {summary.ct_stop ? `${summary.ct_stop.stage} oid=${summary.ct_stop.oid} trig=${summary.ct_stop.trigger}` : '(none)'}
            </div>
            <div style={{ gridColumn: '1 / -1' }} data-testid="diagnostics-api-budget">
              <b>API budget:</b> {summary.api_budget
                ? `${summary.api_budget.remaining?.toLocaleString?.() ?? summary.api_budget.remaining} remaining (${summary.api_budget.used?.toLocaleString?.() ?? summary.api_budget.used} used / ${summary.api_budget.cap?.toLocaleString?.() ?? summary.api_budget.cap} cap) · status=${summary.api_budget.status}${summary.api_budget.stale ? ` · STALE (${summary.api_budget.stale_reason}, ${Math.round(summary.api_budget.stale_since_sec || 0)}s ago)` : ''} · burn_1h=${summary.api_budget.burn_rate_1h_req != null ? Math.round(summary.api_budget.burn_rate_1h_req) + ' req/h' : '—'} · last_replenish=${summary.api_budget.last_replenish_ts ? new Date(summary.api_budget.last_replenish_ts * 1000).toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z') : '—'}`
                : (data.api_budget?.reason || '(unavailable)')}
            </div>
          </div>
        </div>
      )}
      {data && (
        <pre style={pre} data-testid="diagnostics-output">
{JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default DiagnosticsPanel;
